import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import type { IntentName } from "@/design";
import { Badge } from "@/shared/ui/badge";
import { Modal } from "@/shared/ui/modal";

import {
  CHANNELS,
  channelLabelKey,
  type ChannelPreviews,
  type ChannelTestResult,
} from "../lib/settings";

const RESULT_INTENT: Record<ChannelTestResult["status"], IntentName> = {
  sent: "success",
  failed: "danger",
  skipped: "neutral",
};

interface ChannelPreviewModalProps {
  open: boolean;
  onClose: () => void;
  previews: ChannelPreviews | null;
  results?: ChannelTestResult[] | null;
}

export function ChannelPreviewModal({
  open,
  onClose,
  previews,
  results,
}: ChannelPreviewModalProps): JSX.Element | null {
  const { t } = useTranslation("notifications");

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("settings.preview_title")}
      width={560}
      footer={
        <button
          type="button"
          onClick={onClose}
          className="rounded-[10px] border border-[var(--border-default)] px-4 py-2.5 text-sm font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-sunken)]"
        >
          {t("settings.close")}
        </button>
      }
    >
      <div data-testid="channel-preview-modal" className="grid grid-cols-2 gap-4">
        {CHANNELS.map((c) => {
          const result = results?.find((r) => r.channel === c) ?? null;
          return (
            <div key={c} className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-semibold text-[var(--text-secondary)]">
                  {t(channelLabelKey(c) as never)}
                </div>
                {result ? (
                  <Badge intent={RESULT_INTENT[result.status]} dot>
                    {t(`settings.test_result_${result.status}` as never)}
                  </Badge>
                ) : null}
              </div>
              <div className="rounded-lg border border-[var(--premium-border)] bg-[var(--surface-sunken)] p-3 text-xs leading-relaxed text-[var(--text-primary)]">
                {previews?.[c] ?? "—"}
              </div>
              {result?.detail ? (
                <div
                  data-testid={`channel-result-detail-${c}`}
                  className="text-xs text-[var(--text-subtle)]"
                >
                  {result.detail}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
